import { ChangeDetectorRef, Component, ElementRef, Injector, OnDestroy, OnInit, ViewChild, afterNextRender, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { ConfirmationService } from '../../core/services/confirmation.service';
import { ConfirmationOptions } from '../../core/models/confirmation-options.model';

@Component({
  selector: 'app-confirmation-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule],
  templateUrl: './confirmation-dialog.component.html',
  styleUrl: './confirmation-dialog.component.css'
})
export class ConfirmationDialogComponent implements OnInit, OnDestroy {
  isVisible = false;
  options: ConfirmationOptions | null = null;
  selectedChoice: unknown = null;
  textInput: string = '';
  dateInput: string = '';
  dateInput2: string = '';
  submitted = false;
  private subscription?: Subscription;
  private readonly injector = inject(Injector);
  private readonly cdr = inject(ChangeDetectorRef);
  /** L'élément qui avait le focus à l'ouverture : il le retrouve à la fermeture. */
  private focusAvant: HTMLElement | null = null;
  @ViewChild('dialogue') private dialogue?: ElementRef<HTMLElement>;

  constructor(private confirmationService: ConfirmationService) { }

  ngOnInit() {
    this.subscription = this.confirmationService.confirmation$.subscribe(
      (options) => {
        const ouverture = !!options && !this.isVisible;
        const fermeture = !options && this.isVisible;
        this.options = options;
        this.isVisible = !!options;
        this.selectedChoice = null;
        this.textInput = '';
        this.dateInput = '';
        this.dateInput2 = '';
        this.submitted = false;
        // L'état change hors de la détection de changements : la signaler.
        this.cdr.markForCheck();
        if (ouverture) this.ouvrir();
        if (fermeture) this.focusAvant?.focus();
      }
    );
  }

  /**
   * Accessibilité : le focus va sur « Annuler » (le choix sans conséquence)
   * et reste dans le dialogue tant qu'il est ouvert ; Échap annule.
   */
  private ouvrir(): void {
    const actif = document.activeElement;
    this.focusAvant = actif instanceof HTMLElement ? actif : null;
    afterNextRender(
      () => this.dialogue?.nativeElement.querySelector<HTMLElement>('.btn-cancel')?.focus(),
      { injector: this.injector },
    );
  }

  clavier(e: KeyboardEvent): void {
    if (e.key === 'Escape') {
      e.preventDefault();
      this.onCancel();
      return;
    }
    if (e.key !== 'Tab' || !this.dialogue) return;
    const focusables = Array.from(
      this.dialogue.nativeElement.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), select:not([disabled])',
      ),
    );
    if (focusables.length === 0) return;
    const premier = focusables[0];
    const dernier = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === premier) {
      e.preventDefault();
      dernier.focus();
    } else if (!e.shiftKey && document.activeElement === dernier) {
      e.preventDefault();
      premier.focus();
    }
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  getIconClass(): string {
    return this.options?.type || 'danger';
  }

  getConfirmButtonClass(): string {
    return this.options?.type || 'danger';
  }

  onConfirm() {
    this.submitted = true;
    if ((this.options?.selectRequired && !this.selectedChoice) || (this.options?.inputRequired && !this.textInput)) {
      return;
    }

    // Return extended object with all possible values
    this.confirmationService.respond(true, {
      value: this.selectedChoice,
      text: this.textInput,
      date: this.dateInput,
      date2: this.dateInput2
    });
  }

  onCancel() {
    this.confirmationService.respond(false);
  }

  onOverlayClick() {
    this.onCancel();
  }
}
